/** Generic customer list screen: loading / error / empty / data states, responsive table, EN/AR. */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { DataTable, EmptyState, ErrorState, Loading, PageHeader, type Column } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { useResource } from '../../../lib/resource';

export type Row = { id: string } & Record<string, unknown>;

export function rowsOf(data: unknown): Row[] {
  const arr = Array.isArray(data) ? data : (data as { items?: unknown[] } | null)?.items ?? [];
  return (arr as Record<string, unknown>[]).map((r, i) => ({ ...r, id: String(r.id ?? i) }));
}

/** Primary next step offered on an empty list (e.g. connect a source); the screen's own actions stay in the header. */
export interface EmptyAction { to: string; labelKey: string }

export function ListScreen({ titleKey, introKey, path, columns, emptyKey, emptyAction, actions, children }: {
  titleKey: string; introKey?: string; path: `/v1/${string}`; columns: Column<Row>[]; emptyKey: string; emptyAction?: EmptyAction; actions?: ReactNode; children?: ReactNode;
}) {
  const { t } = useApp();
  const res = useResource<unknown>(path);
  const workspace = useWorkspaceName();
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={workspace} title={t(titleKey)} intro={introKey ? t(introKey) : undefined} actions={actions} />
      {children}
      <ScreenBody res={res} caption={t(titleKey)} columns={columns} emptyKey={emptyKey} emptyAction={emptyAction} />
    </section>
  );
}

export function ScreenBody({ res, caption, columns, emptyKey, emptyAction }: { res: ReturnType<typeof useResource<unknown>>; caption: string; columns: Column<Row>[]; emptyKey: string; emptyAction?: EmptyAction }) {
  const { t } = useApp();
  if (res.status === 'loading') return <Loading label={t('common.loading')} />;
  if (res.status === 'error') return <ErrorState message={t('shell.error.load')} />;
  const rows = rowsOf(res.data);
  if (!rows.length) {
    return (
      <EmptyState title={t(emptyKey)}>
        {emptyAction ? <Link to={emptyAction.to} className="qm-button" data-variant="primary">{t(emptyAction.labelKey)}</Link> : null}
      </EmptyState>
    );
  }
  return <DataTable caption={caption} columns={columns} rows={rows} />;
}

/** Active workspace name for the editorial page eyebrow (falls back to the generic "Workspace" label). */
export function useWorkspaceName(): string {
  const { t, me } = useApp();
  return me?.memberships?.find((m) => m.tenantId === me.tenantId)?.tenantName || t('shell.page.eyebrow');
}

export const str = (v: unknown) => (v == null ? '' : String(v));
